import { differenceInCalendarDays, parseISO } from "date-fns";
import { useEffect } from "react";

import { useAppDispatch } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { useSceneId } from "hooks/useSceneId";
import { telemetry } from "telemetry";

import { offlineActions } from "./offlineSlice";
import { useIsProjectScene, useOfflineSceneKey } from "./useOfflineSceneKey";

type SceneHistory = {
    visits: string[];
    incrementalTriggered: boolean;
};

const emptyHistory: SceneHistory = {
    visits: [],
    incrementalTriggered: false,
};
const visitThreshold = 3;
const periodDays = 30;
let handled = false;

/**
 * Standalone's auto-incremental: a project scene with nothing offline yet, visited
 * {@link visitThreshold} times within {@link periodDays} days, is enrolled for incremental caching.
 */
export function useHandleAutoIncremental() {
    const {
        state: { offlineWorkerState },
    } = useExplorerGlobals();

    const sceneId = useSceneId();
    const sceneKey = useOfflineSceneKey();
    const isProjectScene = useIsProjectScene();
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (handled || !offlineWorkerState || isProjectScene === undefined) {
            return;
        }

        handled = true;
        if (!isProjectScene) {
            return;
        }

        void (async () => {
            const state = (await offlineWorkerState.getScene(sceneKey)?.getState()) ?? { kind: "absent" };
            if (state.kind !== "absent") {
                return;
            }

            const history = getSceneHistory(sceneId);

            if (history.incrementalTriggered) {
                return;
            }

            const today = new Date();
            history.visits = history.visits.filter((visit) => {
                return differenceInCalendarDays(today, parseISO(visit)) <= periodDays;
            });
            history.visits.push(today.toISOString());

            if (history.visits.length >= visitThreshold) {
                history.incrementalTriggered = true;
                dispatch(offlineActions.setAction({ action: "enrolIncremental" }));
            }

            localStorage.setItem(getStorageKey(sceneId), JSON.stringify(history));
        })().catch((e) => {
            telemetry.trackCaughtException({
                source: "offline.useHandleAutoIncremental",
                category: "data",
                error: e,
            });
        });
    }, [sceneId, sceneKey, isProjectScene, offlineWorkerState, dispatch]);
}

/** Test-only: let the next mount run again, as a fresh page load would. */
export function resetAutoIncrementalForTests() {
    handled = false;
}

function getStorageKey(sceneId: string) {
    return `auto_incremental_loading/${sceneId}`;
}

function getSceneHistory(sceneId: string): SceneHistory {
    const jsonStr = localStorage.getItem(getStorageKey(sceneId));

    if (!jsonStr) {
        return { ...emptyHistory, visits: [] };
    }

    try {
        return JSON.parse(jsonStr) as SceneHistory;
    } catch (e) {
        console.warn(e);
        telemetry.trackCaughtException({
            source: "offline.useHandleAutoIncremental.getSceneHistory",
            category: "data",
            error: e,
        });

        return { ...emptyHistory, visits: [] };
    }
}
